import { useEffect } from 'react'
import { RotateCcw, Trash2, X } from 'lucide-react'
import { Avatar } from './Avatar'
import { formatDoneAt } from '../lib/dates'
import type { Card } from '../lib/types'

type Props = {
  cards: Card[]
  filtered: boolean
  onClose: () => void
  onRestore: (card: Card) => void
  onDelete: (card: Card) => void
}

export function DoneDrawer({ cards, filtered, onClose, onRestore, onDelete }: Props) {
  useEffect(() => {
    const escape = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', escape)
    return () => document.removeEventListener('keydown', escape)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-ink/30"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
    >
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Recently done"
        className="flex h-full w-full max-w-sm flex-col bg-cream shadow-xl"
      >
        <header className="flex items-center justify-between border-b border-cream-200 px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold tracking-wide text-ink uppercase">Done</h2>
            <p className="text-xs text-muted">
              {cards.length === 0
                ? 'Nothing ticked off yet'
                : `${cards.length} recently ticked off`}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="grid size-8 place-items-center rounded-lg text-muted transition hover:bg-cream-100"
          >
            <X className="size-4" />
          </button>
        </header>

        <div className="flex-1 space-y-2 overflow-y-auto p-4">
          {cards.length === 0 && (
            <p className="px-2 py-10 text-center text-sm text-faint">
              {filtered ? 'Nothing done by this person yet' : 'Nothing done yet'}
            </p>
          )}

          {cards.map((card) => (
            <div key={card.id} className="rounded-xl bg-white p-3 ring-1 ring-cream-200">
              <p className="text-sm leading-snug break-words text-muted line-through">{card.title}</p>

              <div className="mt-2 flex items-center gap-2">
                <Avatar id={card.assignee} />
                {card.doneAt && (
                  <span className="text-xs text-faint">{formatDoneAt(card.doneAt)}</span>
                )}

                <div className="ml-auto flex items-center gap-0.5">
                  <button
                    type="button"
                    onClick={() => onRestore(card)}
                    aria-label={`Put "${card.title}" back on the board`}
                    title="Put back on the board"
                    className="grid size-7 place-items-center rounded-lg text-muted transition
                               hover:bg-teal-50 hover:text-teal"
                  >
                    <RotateCcw className="size-3.5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => onDelete(card)}
                    aria-label={`Delete "${card.title}"`}
                    title="Delete for good"
                    className="grid size-7 place-items-center rounded-lg text-muted transition
                               hover:bg-flame/10 hover:text-flame"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </aside>
    </div>
  )
}
